import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  FormFeedback,
  Spinner,
  Collapse,
  Col,
  Row,
} from "reactstrap";
import { Mail, Phone, MapPin, ArrowLeft } from "lucide-react";
import VendorLogo from "../components/VendorLogo";
import SecondaryLogo from "../components/SecondaryLogo";
import "../styles/payment-form.css";
// import axios from "axios";
import api from "../api"
import NotFound from "./NotFound";
import nttlogo from "../assets/images/adaptis-dark.webp";

const TransactionWarningBanner = () => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 9000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div className="txn-warning-banner">
      <span className="txn-warning-icon">⚠️</span>
      <span className="txn-warning-text">
        Do not refresh or close this tab — your payment may be affected.
      </span>
    </div>
  );
};

const SkeletonBox = ({ width = "100%", height = "16px", className = "", style = {} }) => (
  <div className={`skeleton-box ${className}`} style={{ width, height, ...style }} />
);

const VendorStripSkeleton = () => (
  <div className="vendor-strip d-none d-md-flex flex-column">
    <main className="flex-grow-1 vendor-content">
      <div className="vendor-logo-wrapper">
        <SkeletonBox width="64px" height="64px" style={{ borderRadius: "12px" }} />
        <SkeletonBox width="60%" height="22px" style={{ marginTop: "12px" }} />
      </div>
      <SkeletonBox width="80%" height="14px" style={{ marginTop: "12px" }} />
      <SkeletonBox width="40%" height="32px" style={{ marginTop: "16px", borderRadius: "8px" }} />
      <SkeletonBox width="50%" height="14px" style={{ marginTop: "12px", borderRadius: "20px" }} />
    </main>
    <SkeletonBox width="90%" height="14px" style={{ margin: "0 auto 16px" }} />
  </div>
);

const MobileHeaderSkeleton = () => (
  <div className="mobile-header d-md-none">
    <Row className="g-0 align-items-center">
      <Col xs="3" className="paymentpage-mobile-logo">
        <SkeletonBox width="48px" height="48px" style={{ borderRadius: "10px" }} />
      </Col>
      <Col xs="9">
        <SkeletonBox width="70%" height="16px" />
        <SkeletonBox width="40%" height="12px" style={{ marginTop: "8px" }} />
        <SkeletonBox width="30%" height="12px" style={{ marginTop: "8px" }} />
      </Col>
    </Row>
  </div>
);

const SearchStepSkeleton = () => (
  <div className="d-flex flex-column h-100 justify-content-center pt-5">
    <div className="mt-5">
      <SkeletonBox width="45%" height="14px" style={{ marginBottom: "8px" }} />
      <SkeletonBox width="100%" height="40px" style={{ borderRadius: "8px" }} />
      <SkeletonBox width="100%" height="44px" style={{ marginTop: "24px", borderRadius: "8px" }} />
    </div>
    <div className="form-footer">
      <div className="powered-by text-center mt-2">
        <SkeletonBox width="80px" height="24px" style={{ margin: "0 auto", borderRadius: "4px" }} />
      </div>
    </div>
  </div>
);

const FieldSkeleton = ({ type = "text" }) => (
  <FormGroup className="mb-3">
    <SkeletonBox width="40%" height="14px" style={{ marginBottom: "8px" }} />
    {type === "radio" ? (
      <div style={{ display: "flex", gap: "16px" }}>
        <SkeletonBox width="80px" height="20px" style={{ borderRadius: "20px" }} />
        <SkeletonBox width="80px" height="20px" style={{ borderRadius: "20px" }} />
      </div>
    ) : (
      <SkeletonBox width="100%" height="40px" style={{ borderRadius: "8px" }} />
    )}
  </FormGroup>
);

const FormStepSkeleton = ({ count = 5 }) => {
  const types = ["text", "text", "radio", "text", "text", "text", "radio", "text"];
  return (
    <Form className="d-flex flex-column h-100" noValidate>
      <div className="form-scroll">
        {Array.from({ length: count }, (_, i) => (
          <FieldSkeleton key={i} type={types[i % types.length]} />
        ))}
      </div>
      <div className="form-footer">
        <SkeletonBox width="100%" height="48px" style={{ borderRadius: "8px" }} />
        <div className="powered-by text-center mt-2">
          <SkeletonBox width="80px" height="24px" style={{ margin: "0 auto", borderRadius: "4px" }} />
        </div>
      </div>
    </Form>
  );
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;
const AMOUNT_REGEX = /^\d+(\.\d{0,2})?$/;
const MAX_CHAR_LIMIT = 250;
const MAX_ADDRESS_CHAR_LIMIT = 500;

function getFieldError(f, val) {
  const key = f.name || f.id;

  if (f.type === "phone") {
    if (!val || val.trim() === "") return f.required || f.mandatory ? "This field is required" : "";
    if (!/^\d+$/.test(val)) return "Phone number must contain digits only.";
    if (!PHONE_REGEX.test(val)) return "Phone number must be exactly 10 digits.";
    return "";
  }

  if (f.type === "email") {
    if (!val || val.trim() === "") return f.required || f.mandatory ? "This field is required" : "";
    if (!EMAIL_REGEX.test(val)) return "Must be a valid email address.";
    return "";
  }

  if (f.type === "amount") {
    if (!val || val.trim() === "") return f.required || f.mandatory ? "This field is required" : "";
    if (isNaN(val) || Number(val) < 0) return "Amount must be a positive number.";
    if (!AMOUNT_REGEX.test(val)) return "Amount must be a valid number (up to 2 decimal places).";
    return "";
  }

  if (f.type === "address") {
    if (!val || val.trim() === "") return f.required || f.mandatory ? "This field is required" : "";
    if (val.length > MAX_ADDRESS_CHAR_LIMIT) return `Maximum ${MAX_ADDRESS_CHAR_LIMIT} characters allowed.`;
    return "";
  }

  if ((f.required || f.mandatory) && (!val || String(val).trim() === "")) {
    return "This field is required";
  }

  return "";
}

const normalizeDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return "";

  const cleaned = dateStr.trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) return cleaned;

  const parts = cleaned.split(/[-\/]/).map(p => p.trim().padStart(2, '0'));

  if (parts.length !== 3) {
    const fallback = new Date(cleaned);
    if (!isNaN(fallback.getTime())) {
      return fallback.toISOString().split('T')[0];
    }
    console.warn(`Invalid date format (expected MM/DD/YYYY): ${dateStr}`);
    return cleaned;
  }

  let [month, day, year] = parts;

  if (year.length === 2) {
    const yy = parseInt(year);
    year = yy < 70 ? "20" + year : "19" + year;
  }

  const m = parseInt(month);
  const d = parseInt(day);
  const y = parseInt(year);

  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) {
    console.warn(`Suspicious date values: ${dateStr}`);
    return cleaned; // fallback
  }

  return `${year}-${month}-${day}`;
};

export default function PublicForm() {
  const { slug } = useParams();
  const location = useLocation();

  const [form, setForm] = useState(null);
  const [form_request, setFormRequest] = useState({});
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  const [searchValue, setSearchValue] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [step, setStep] = useState("search");

  const [schemaLoading, setSchemaLoading] = useState(true);
  const [loadingRecord, setLoadingRecord] = useState(false);

  const isNavigatingAway = useRef(false);

  useEffect(() => {
    const handlePaymentCancelled = () => {
      setFormDisabled(false);
      setStatus("");
      isNavigatingAway.current = false;
    };
    window.addEventListener("paymentCancelled", handlePaymentCancelled);
    return () => window.removeEventListener("paymentCancelled", handlePaymentCancelled);
  }, []);

  useEffect(() => {
    const load = async () => {
      const isActiveStatus = location.state?.isActive ?? "true";
      try {
        await api.get("/csrf");
        const response = await api.post(
          `${process.env.REACT_APP_BASE_URL_DEV}/forms/slug/init/${slug}`,
          {},
          {
            params: { isActive: isActiveStatus },
            headers: { "Content-Type": "application/json" },
          },
        );

        const data = response.data;
        setForm(data?.form_schema ? data : { notFound: true });

        if (data?.mode !== "excel") {
          setStep("form");
        }
      } catch (err) {
        console.error("Failed to load form:", err);
        setForm({ notFound: true });
      } finally {
        setSchemaLoading(false);
      }
    };
    load();
  }, [slug, location.state]);

  const fields = useMemo(() => form?.form_schema?.fields || [], [form]);

  // Dynamically locate the email/phone/amount fields purely by type (Amount
  // additionally by its reserved mandatory id, since it's the only field
  // still guaranteed to exist and be locked). Email and Phone are ordinary
  // optional fields now — they may or may not be present on a given form,
  // so these lookups gracefully resolve to null when absent.
  const amountField = useMemo(
    () => fields.find((f) => f.id === "mandatory-amount" || (f.mandatory && f.type === "amount")) || null,
    [fields],
  );
  const emailField = useMemo(
    () => fields.find((f) => f.type === "email") || null,
    [fields],
  );
  const phoneField = useMemo(
    () => fields.find((f) => f.type === "phone") || null,
    [fields],
  );

  const amountKey = amountField?.id ?? "mandatory-amount";

  let displayAmount = form_request[amountKey] ?? "0";
  if (typeof displayAmount === "string" && displayAmount.trim().toLowerCase() === "null") {
    displayAmount = "0";
  }

  const mode = form?.mode ?? "";
  const searchFieldId = form?.searchKey ?? null;
  const isExcelMode = mode === "excel" && searchFieldId;

  const descriptionText = form?.description?.trim() ? form.description : "Secure payment form";

  const buildFieldMap = (fields) => {
    const map = {};
    fields.forEach((f) => {
      map[f.id] = f.id;
      if (f.rawHeader) {
        const normalized = f.rawHeader.toLowerCase().replace(/\s+/g, "_");
        map[normalized] = f.id;
      }
      if (f.id === "mandatory-amount" || (f.mandatory && f.type === "amount")) {
         map["amount"]  = f.id;
      }
      if (f.type === "phone") {
        map["mobile"] = map["phone"] = map["mobile_number"] = f.id;
      }
      if (f.type === "email") {
        map["email"] = map["email_id"] = f.id;
      }
      if (f.type === "address") {
        map["address"] = map["addr"] = f.id;
      }
    });
    return map;
  };

  const parseAndMapRecord = (record, fields) => {
    let dataObj = {};
    try {
      dataObj = typeof record.data === "string" ? JSON.parse(record.data) : record.data;
    } catch (e) {
      console.error("Failed to parse record.data:", e);
      return {};
    }

    const fieldMap = buildFieldMap(fields);
    const mapped = {};

    Object.entries(dataObj).forEach(([excelKey, value]) => {
      const normalizedKey = excelKey.toLowerCase().replace(/\s+/g, "_");
      const fieldId = fieldMap[normalizedKey] || fieldMap[excelKey] || excelKey;

      let finalValue = value != null ? String(value) : "";

      // Normalize date fields
      if (finalValue) {
        const fieldDef = fields.find(f => f.id === fieldId);
        if (fieldDef && fieldDef.type === "date") {
          finalValue = normalizeDate(finalValue);
        }
      }

      mapped[fieldId] = finalValue;
    });

    return mapped;
  };

  const applyFixedValues = (flds, base = {}) => {
    const result = { ...base };
    flds.forEach((f) => {
      if (f.fixedValueEnabled && f.fixedValue !== undefined && f.fixedValue !== "") {
        result[f.id] = String(f.fixedValue);
      }
    });
    return result;
  };

  const recalcSplitAmount = (fields, formRequest) => {
    const splitFields = fields.filter((f) => f.type === "amount" && f.splitRequired);
    if (!splitFields.length) return null;
    return splitFields.reduce((sum, f) => sum + (Number(formRequest[f.id]) || 0), 0);
  };

  useEffect(() => {
    if (!fields.length || isExcelMode) return;
    setFormRequest((prev) => {
      const withFixed = applyFixedValues(fields, prev);
      const total = recalcSplitAmount(fields, withFixed);
      if (total !== null && amountField) withFixed[amountField.id] = String(total);
      return withFixed;
    });
  }, [fields, isExcelMode, amountField]);

  const fetchBySearchKey = async (value) => {
    if (!value?.trim()) {
      setSearchError("Please enter search key number");
      return;
    }

    setSearching(true);
    setSearchError("");

    try {
      const formId = form?.formId ?? "";
      const response = await api.post(
        `${process.env.REACT_APP_BASE_URL_DEV}/forms/slug/${slug}/content`,
        {},
        {
          params: { searchKey: value.trim(), formId },
          headers: { "Content-Type": "application/json" },
        },
      );

      const record = response.data;

      if (!record || !record.data) {
        setSearchError("No records found");
        return;
      }

      setLoadingRecord(true);
      const mapped = parseAndMapRecord(record, fields);
      if (searchFieldId) {
        mapped[searchFieldId] = value;
      }

      const withFixed = applyFixedValues(fields, mapped);

      const total = recalcSplitAmount(fields, withFixed);
      if (total !== null && amountField) {
        withFixed[amountField.id] = String(total);
      }

      setFormRequest(withFixed);
      setStep("form");
      setSearchError("");
    } catch (err) {
      const msg =
        err.response?.status === 404
          ? "No records found."
          : err.response?.data?.error || "Failed to fetch record.";
      setSearchError(msg);
    } finally {
      setSearching(false);
      setLoadingRecord(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setSearchError("");
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchBySearchKey(searchValue);
  };

  const handleBackToSearch = () => {
    setStep("search");
  };
  const clearChildren = (
    parentId,
    updated
  ) => {
    fields.forEach((field) => {
      if (
        field.parentFieldId === parentId
      ) {
        updated[field.id] = "";

        clearChildren(
          field.id,
          updated
        );
      }
    });
  };
  const handleFieldChange = (key, value, fieldType) => {
    let processedValue = value;

    if (fieldType === "checkbox") {
      processedValue = value;
    }
    else if (fieldType === "date") {
      processedValue = normalizeDate(String(processedValue));
    }

    else if (fieldType === "phone") {
      processedValue = processedValue.replace(/\D/g, "").slice(0, 10);
    } else if (fieldType === "address") {
      processedValue = processedValue.slice(0, MAX_ADDRESS_CHAR_LIMIT);
    } else if (typeof processedValue === "string") {
      processedValue = processedValue.slice(
        0,
        MAX_CHAR_LIMIT
      );
    }
   

    setFormRequest((prev) => {
      const updated = { ...prev, [key]: processedValue };
      clearChildren(
        key,
        updated
      );
     
      fields.forEach((field) => {
        if (
          field.cascadeEnabled &&
          field.parentFieldId === key
        ) {
          updated[field.id] = "";
        }
      });
      const total = recalcSplitAmount(fields, updated);
      if (total !== null && amountField) {
        updated[amountField.id] = String(total);
      }
      return updated;
    });

    setErrors((prev) => {
      const field = fields.find((f) => (f.name || f.id) === key);
      if (!field) return prev;
      const err = getFieldError(field, processedValue);
      return { ...prev, [key]: err };
    });
  };

  // Fully data-driven: only checks the email/phone/amount fields that
  // actually exist on this form's schema, and only enforces "required" for
  // fields explicitly marked required/mandatory by the form builder. Amount
  // is the only field type that's always present and always required.
  const areRequiredFieldsFilled = useMemo(() => {
    if (amountField) {
      const amtVal = form_request[amountField.id];
      if (!amtVal || isNaN(amtVal) || Number(amtVal) <= 0) return false;
    }

    const splitFields = fields.filter((f) => f.type === "amount" && f.splitRequired);
    for (const f of splitFields) {
      const val = form_request[f.id];
      if (val === undefined || val === null || String(val).trim() === "") continue;
      const num = Number(val);
      if (isNaN(num) || num < 0) return false;
    }

    if (splitFields.length > 0 && amountField) {
      const total = splitFields.reduce((sum, f) => sum + (Number(form_request[f.id]) || 0), 0);
      if (Number(form_request[amountField.id]) !== total) return false;
    }

    const requiredFields = fields.filter((f) => (f.required || f.mandatory) && f.id !== amountField?.id);
    return requiredFields.every((f) => {
      const key = f.name || f.id;
      const val = form_request[key];
      if (f.type === "checkbox") return !!val;
      if (f.type === "email") return val && EMAIL_REGEX.test(val);
      if (f.type === "phone") return val && PHONE_REGEX.test(val);
      if (f.type === "amount") return val && !isNaN(val) && Number(val) >= 0;
      if (f.type === "address") return val && String(val).trim() !== "";
      return val !== undefined && val !== null && String(val).trim() !== "";
    });
  }, [fields, form_request, amountField]);

  const validate = () => {
    const e = {};
    fields.forEach((f) => {
      const key = f.name || f.id;
      const val = form_request[key] ?? "";
      const err = getFieldError(f, val);
      if (err) e[key] = err;
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const initiatePayment = async () => {
    if (!validate()) return;



    const merchantId = form?.merchantId ?? null;
    const formId = form?.formId ?? null;
    const title = form?.title ?? null;

    // Collect label + value for any field explicitly marked receiptEnabled,
    // so the receipt can render exactly the fields the form builder chose.
    const receiptFields = fields
      .filter((f) => f.receiptEnabled)
      .map((f) => {
        const key = f.name || f.id;
        let val = form_request[key] ?? "";
        if (typeof val === "string" && val.trim().toLowerCase() === "null") {
          val = "";
        }
        return { label: f.label, value: val };
      });

    const payload = { formId, slug, merchantId, form_request, receiptFields, title };

    try {
      setLoading(true);
      const res = await api.post(
        `${process.env.REACT_APP_BASE_URL_DEV}/payment/auth`, payload, {
        headers: { "Content-Type": "application/json" }

      }
      );

      // if (!res.ok) {
      //   const data = await res.json().catch(() => ({}));
      //   setStatus(data.error || "Submission failed");
      //   return;
      // }

      const data = res.data;
      window.openPay(
        data.atomTokenId,
        merchantId,
        emailField ? form_request[emailField.id] ?? "" : "",
        phoneField ? form_request[phoneField.id] ?? "" : "",
        form?.env,
      );
      setFormDisabled(true);
      sessionStorage.setItem("atomTokenId", data.atomTokenId);
    } catch (err) {

      setStatus("Payment request failed");
    } finally {
      setLoading(false);
    }
  };

  if (schemaLoading) {
    return (
      <div className="checkout-wrapper native-ui">
        <div className="checkout-card">
          <MobileHeaderSkeleton />
          <VendorStripSkeleton />
          <div className="checkout-form position-relative">
            <SearchStepSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (!form || form.notFound) return <NotFound />;

  const isFieldDisabled = loading || formDisabled;
  const getVisibleOptions = (field) => {
   

    if (
      !field.cascadeEnabled ||
      !field.parentFieldId ||
      !field.cascadeMappings
    ) {
      return field.options || [];
    }

    const parentValue =
      form_request[field.parentFieldId];

    if (!parentValue) {
      return [];
    }

    // Checkbox parent
    if (Array.isArray(parentValue)) {
      const merged = new Set();

      parentValue.forEach((value) => {
        const mapped =
          field.cascadeMappings[value] || [];

        mapped.forEach((option) => {
          merged.add(option);
        });
      });

      return [...merged];
    }

    // Dropdown / Radio parent
    return (
      field.cascadeMappings[parentValue] || []
    );
  };
 const shouldShowField = (field) => {
  if (!field.cascadeEnabled) {
    return true;
  }

  if (!field.parentFieldId) {
    return true;
  }

  const parentValue =
    form_request[field.parentFieldId];

  if (
    parentValue === undefined ||
    parentValue === null ||
    parentValue === ""
  ) {
    return false;
  }

  // Checkbox parent
  if (Array.isArray(parentValue)) {
    const hasMappedValues =
      parentValue.some(
        (value) =>
          (
            field.cascadeMappings?.[
              value
            ] || []
          ).length > 0
      );

    return hasMappedValues;
  }

  // Dropdown / Radio parent
  return (
    (
      field.cascadeMappings?.[
        parentValue
      ] || []
    ).length > 0
  );
};

  const renderField = (f) => {
    const key = f.name || f.id;
    let val = form_request[key] ?? "";

    if (typeof val === "string" && val.trim().toLowerCase() === "null") {
      val = "";
    }

    const invalid = !!errors[key];
    const errorMsg = errors[key] || "";
    const isSearchField = key === searchFieldId;
    const disabled = isFieldDisabled || (isSearchField && isExcelMode) || f.readOnly === true;
    const options = getVisibleOptions(f);
    const currentLength = String(val).length;

    if (f.type === "dropdown" || f.type === "select") {
      return (
        <FormGroup key={key} className="mb-3">
          <Label htmlFor={key}>
            {f.label} {f.required || f.mandatory ? "*" : ""}
          </Label>
          <Input
            id={key}
            type="select"
            value={val}
            onChange={(e) => handleFieldChange(key, e.target.value, f.type)}
            invalid={invalid}
            disabled={disabled}
            className="custom-select"
          >
            <option value="">-- Select --</option>
            {options.map((opt) => {
              const optLabel = typeof opt === "object" ? (opt.label ?? opt.value) : opt;
              const optValue = typeof opt === "object" ? opt.value : opt;
              return <option key={optValue} value={optValue}>{optLabel}</option>;
            })}
          </Input>
          {invalid && <FormFeedback className="d-block">{errorMsg}</FormFeedback>}
          {f.help && <small className="form-text text-muted">{f.help}</small>}
        </FormGroup>
      );
    }

    if (f.type === "radio") {
      return (
        <FormGroup key={key} className="mb-3">
          <Label className="d-block mb-2">
            {f.label} {f.required || f.mandatory ? "*" : ""}
          </Label>
          <div className="radio-group-row">
            {options.map((opt) => {
              const optLabel = typeof opt === "object" ? (opt.label ?? opt.value) : opt;
              const optValue = typeof opt === "object" ? opt.value : opt;
              return (
                <div key={optValue} className="form-check form-check-inline">
                  <Input
                    type="radio"
                    id={`${key}-${optValue}`}
                    name={key}
                    value={optValue}
                    checked={val === optValue}
                    onChange={() => handleFieldChange(key, optValue, f.type)}
                    disabled={disabled}
                    className="form-check-input"
                  />
                  <Label className="form-check-label" htmlFor={`${key}-${optValue}`}>
                    {optLabel}
                  </Label>
                </div>
              );
            })}
          </div>
          {invalid && <FormFeedback className="d-block mt-1">{errorMsg}</FormFeedback>}
          {f.help && <small className="form-text text-muted">{f.help}</small>}
        </FormGroup>
      );
    }

    // if (f.type === "checkbox") {
    //   return (
    //     <FormGroup key={key} className="mb-3">
    //       <div className="checkbox-field-wrapper">
    //         <Input
    //           type="checkbox"
    //           id={key}
    //           checked={!!val && val !== "false"}
    //           onChange={(e) => handleFieldChange(key, e.target.checked ? "True" : "", f.type)}
    //           disabled={disabled}
    //           className="form-check-input custom-checkbox"
    //         />
    //         <Label htmlFor={key} className="checkbox-label mb-0">
    //           {f.label} {f.required || f.mandatory ? "*" : ""}
    //         </Label>
    //       </div>
    //       {invalid && <FormFeedback className="d-block">{errorMsg}</FormFeedback>}
    //       {f.help && <small className="form-text text-muted">{f.help}</small>}
    //     </FormGroup>
    //   );
    // }
    if (f.type === "checkbox") {
      return (
        <FormGroup key={key} className="mb-3">
          <Label className="form-label">
            {f.label}
            {(f.required || f.mandatory) && " *"}
          </Label>

          {(getVisibleOptions(f) || []).map(
            (option) => {
              const selected =
                Array.isArray(val)
                  ? val
                  : [];

              return (
                <div
                  key={option}
                  className="checkbox-field-wrapper"
                >
                  <Input
                    type="checkbox"
                    checked={selected.includes(
                      option
                    )}
                    onChange={(e) => {
                      let updated =
                        [...selected];

                      if (
                        e.target.checked
                      ) {
                        updated.push(
                          option
                        );
                      } else {
                        updated =
                          updated.filter(
                            (x) =>
                              x !== option
                          );
                      }

                      handleFieldChange(
                        key,
                        updated,
                        f.type
                      );
                    }}
                    disabled={disabled}
                  />

                  <Label className="mb-0">
                    {option}
                  </Label>
                </div>
              );
            }
          )}

          {invalid && (
            <FormFeedback className="d-block">
              {errorMsg}
            </FormFeedback>
          )}
        </FormGroup>
      );
    }

    if (f.type === "date") {
      return (
        <FormGroup key={key} className="mb-3">
          <Label htmlFor={key}>
            {f.label} {f.required || f.mandatory ? "*" : ""}
          </Label>
          <Input
            id={key}
            type="date"
            value={val}
            onChange={(e) => handleFieldChange(key, e.target.value, f.type)}
            invalid={invalid}
            disabled={disabled}
          />
          {invalid && <FormFeedback className="d-block">{errorMsg}</FormFeedback>}
          {f.help && <small className="form-text text-muted">{f.help}</small>}
        </FormGroup>
      );
    }


    if (f.type === "phone") {
      return (
        <FormGroup key={key} className="mb-3">
          <Label htmlFor={key}>
            {f.label} {f.required || f.mandatory ? "*" : ""}
          </Label>
          <Input
            id={key}
            type="text"
            value={val}
            onChange={(e) => handleFieldChange(key, e.target.value, "phone")}
            invalid={invalid}
            placeholder={f.placeholder}
            disabled={disabled}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
          />
          {invalid && <FormFeedback className="d-block">{errorMsg}</FormFeedback>}
          {f.help && <small className="form-text text-muted">{f.help}</small>}
        </FormGroup>
      );
    }

    if (f.type === "email") {
      return (
        <FormGroup key={key} className="mb-3">
          <Label htmlFor={key}>
            {f.label} {f.required || f.mandatory ? "*" : ""}
          </Label>
          <Input
            id={key}
            type="email"
            value={val}
            onChange={(e) => handleFieldChange(key, e.target.value, "email")}
            invalid={invalid}
            placeholder={f.placeholder}
            disabled={disabled}
          />
          {currentLength > 200 && (
            <small className={`d-block text-end mt-1 ${currentLength >= MAX_CHAR_LIMIT ? "text-danger" : "text-muted"}`}>
              {currentLength}/{MAX_CHAR_LIMIT}
            </small>
          )}
          {invalid && <FormFeedback className="d-block">{errorMsg}</FormFeedback>}
          {f.help && <small className="form-text text-muted">{f.help}</small>}
        </FormGroup>
      );
    }

    if (f.type === "amount") {
      return (
        <FormGroup key={key} className="mb-3">
          <Label htmlFor={key}>
            {f.label} {f.required || f.mandatory ? "*" : ""}
          </Label>
          <Input
            id={key}
            type="text"
            value={val}
            onChange={(e) => handleFieldChange(key, e.target.value, "amount")}
            invalid={invalid}
            placeholder={f.placeholder}
            disabled={disabled}
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            maxLength={MAX_CHAR_LIMIT}
          />
          {invalid && <FormFeedback className="d-block">{errorMsg}</FormFeedback>}
          {f.help && <small className="form-text text-muted">{f.help}</small>}
        </FormGroup>
      );
    }

    if (f.type === "address") {
      const addrLength = String(val).length;
      return (
        <FormGroup key={key} className="mb-3">
          <Label htmlFor={key}>
            {f.label} {f.required || f.mandatory ? "*" : ""}
          </Label>
          <Input
            id={key}
            type="textarea"
            rows={3}
            value={val}
            onChange={(e) => handleFieldChange(key, e.target.value, "address")}
            invalid={invalid}
            placeholder={f.placeholder || "e.g. Street, City, State, ZIP"}
            disabled={disabled}
            maxLength={MAX_ADDRESS_CHAR_LIMIT}
          />
          {addrLength > MAX_ADDRESS_CHAR_LIMIT - 50 && (
            <small className={`d-block text-end mt-1 ${addrLength >= MAX_ADDRESS_CHAR_LIMIT ? "text-danger" : "text-muted"}`}>
              {addrLength}/{MAX_ADDRESS_CHAR_LIMIT}
            </small>
          )}
          {invalid && <FormFeedback className="d-block">{errorMsg}</FormFeedback>}
          {f.help && <small className="form-text text-muted">{f.help}</small>}
        </FormGroup>
      );
    }

    return (
      <FormGroup key={key} className="mb-3">
        <Label htmlFor={key}>
          {f.label} {f.required || f.mandatory ? "*" : ""}
        </Label>
        <Input
          id={key}
          type={f.type === "number" ? "number" : f.type || "text"}
          value={val}
          onChange={(e) => handleFieldChange(key, e.target.value, f.type)}
          invalid={invalid}
          placeholder={f.placeholder}
          disabled={disabled}
          inputMode={f.type === "number" ? "numeric" : undefined}
          pattern={f.type === "number" ? "[0-9]*" : undefined}
          maxLength={MAX_CHAR_LIMIT}
        />
        {currentLength > 200 && (
          <small className={`d-block text-end mt-1 ${currentLength >= MAX_CHAR_LIMIT ? "text-danger" : "text-muted"}`}>
            {currentLength}/{MAX_CHAR_LIMIT}
          </small>
        )}
        {invalid && <FormFeedback className="d-block">{errorMsg}</FormFeedback>}
        {f.help && <small className="form-text text-muted">{f.help}</small>}
      </FormGroup>
    );
  };

  return (
    <div className="checkout-wrapper native-ui">
      {formDisabled && <TransactionWarningBanner />}

      <div className="checkout-card">
        <div className="mobile-header d-md-none">
          <Row className="g-0 align-items-center">
        

            <Col xs="4" className="text-col">
              <h6 className="mb-0">{form.title}</h6>
              {step === "form" && <small className="d-block">₹ {displayAmount}</small>}
              <Button
                type="button"
                color="link"
                className="view-details p-0 mt-2 text-decoration-none"
                onClick={toggle}
              >
                {isOpen ? "Hide Details ▲" : "View Details ▼"}
              </Button>
            </Col>
              <Col xs="4" className="paymentpage-mobile-logo">
              <div className="logo-row">
                <VendorLogo slug={form.slug} />
                <SecondaryLogo slug={form.slug} />
              </div>
            </Col>
          </Row>

          <Collapse isOpen={isOpen}>
            <div className="mobile-details">
              <div className="detail-row">
                <Mail size={16} className="detail-icon" />
                <span>{form.merchEmail}</span>
              </div>
              <div className="detail-row">
                <Phone size={16} className="detail-icon" />
                <span>{form.merchPhone}</span>
              </div>
              <div className="detail-address">
                <div className="address-text ml-5">{descriptionText}</div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="vendor-strip d-none d-md-flex flex-column">
          <main className="flex-grow-1 vendor-content">
            <div className="vendor-logo-wrapper"> <div className="logo-row"> <VendorLogo slug={form.slug} /> <SecondaryLogo slug={form.slug} /> </div> <h4>{form.title}</h4> </div>
            <p className="vendor-sub">
              {form.merchEmail} · {form.merchPhone}
            </p>
            {step === "form" && <div className="amount">₹ {displayAmount}</div>}
            <span className="secure-badge">🔒 Secured Payments</span>
          </main>
          <div className="vendor-address">{descriptionText}</div>
        </div>

        <div className="checkout-form position-relative">
          {isExcelMode && step === "form" && (
            <Button
              type="button"
              color="link"
              className="position-absolute top-0 start-0 mt-2 ms-3 p-0 text-muted d-flex align-items-center"
              onClick={handleBackToSearch}
              style={{ zIndex: 10, fontSize: "0.95rem", background: "white" }}
            >
              <ArrowLeft size={18} />
              Back
            </Button>
          )}

          {isExcelMode && step === "search" ? (
            <div className="d-flex flex-column h-100 justify-content-center pt-5">
              <Form className="mt-5" onSubmit={handleSearchSubmit}>
                <FormGroup>
                  <Label>
                    {searchFieldId
                      ? searchFieldId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                      : "Search"}{" "}
                    *
                  </Label>
                  <Input
                    value={searchValue}
                    onChange={handleSearchChange}
                    disabled={searching}
                    maxLength={MAX_CHAR_LIMIT}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        fetchBySearchKey(searchValue);
                      }
                    }}
                  />
                  {searchError && (
                    <small className="text-danger d-block mt-1">{searchError}</small>
                  )}
                </FormGroup>
                <Button
                  type="submit"
                  className="premium-cta w-100 mt-4"
                  disabled={searching || !searchValue}
                >
                  {searching ? <><Spinner size="sm" /> Searching...</> : "Next →"}
                </Button>
              </Form>

              <div className="form-footer">
                <div className="powered-by text-center mt-2">
                  <small>Powered by</small>
                  <img src={nttlogo} className="img-fluid" alt="NTT Data" />
                </div>
              </div>
            </div>
          ) : loadingRecord || fields.length === 0 ? (
            <FormStepSkeleton count={fields.length > 0 ? fields.length : 5} />
          ) : (
            <Form className="d-flex flex-column h-100" noValidate>
             <div className="form-scroll">
  {fields
    .filter((f) =>
      shouldShowField(f)
    )
    .map((f) =>
      renderField(f)
    )}
</div>

              <div className="form-footer">
                <Button
                  type="button"
                  className="premium-cta w-100"
                  onClick={initiatePayment}
                  disabled={
                    !displayAmount ||
                    displayAmount === "0" ||
                    !areRequiredFieldsFilled ||
                    loading ||
                    formDisabled
                  }
                >
                  {loading ? (
                    <><Spinner size="sm" className="me-2" /> Processing...</>
                  ) : (
                    `Pay ₹${displayAmount || 0}  →`
                  )}
                </Button>

                <div className="powered-by text-center mt-2">
                  <small>Powered by</small>
                  <img src={nttlogo} className="img-fluid" alt="NTT Data" />
                </div>
              </div>
            </Form>
          )}

          {status && <div className="mt-3 text-danger text-center">{status}</div>}
        </div>
      </div>
    </div>
  );
}
